import { create } from "zustand";
import type { PdfDocument, PdfDocumentKind, PdfSidebarView } from "./types";
import { getRaw, setRaw, StorageKeys } from "@/lib/storage";

let _pdfJsReady: Promise<typeof import("pdfjs-dist")> | null = null;

function getPdfJs() {
  if (!_pdfJsReady) {
    _pdfJsReady = Promise.all([
      import("pdfjs-dist"),
      import("./pdfWorkerSetup"),
    ]).then(([pdfjs]) => pdfjs);
  }
  return _pdfJsReady;
}

const DEFAULT_PANEL_WIDTH = 500;

function readPersistedWidth(): number {
  const stored = getRaw(StorageKeys.pdfPanelWidth);
  if (!stored) return DEFAULT_PANEL_WIDTH;
  const n = Number(stored);
  return Number.isFinite(n) && n >= 300 ? n : DEFAULT_PANEL_WIDTH;
}

export type PdfPendingNavigation = {
  page?: number;
  snippet?: string;
};

export type CitationSnippet = {
  snippet: string;
  page?: number;
};

type PdfDocumentState = {
  documents: PdfDocument[];
  activeDocumentId: string | null;
  panelOpen: boolean;
  panelWidth: number;
  sidebarView: PdfSidebarView;
  pendingNavigations: Map<string, PdfPendingNavigation>;
  citationSnippets: Record<string, CitationSnippet[]>;
  activeCitationSnippet: string | null;
  citationNavTick: number;
  /**
   * Diff HTML by GeneratedDocumentVersion id, lazily filled the first time the
   * user toggles `[Diff]` for a given (doc, version). Keyed by versionId — diff
   * is intrinsically per-version, so a single doc's v1 and v2 caches don't
   * collide and stepping between versions doesn't invalidate the other one.
   */
  diffHtmlByVersion: Record<string, string | null>;

  addLoadingDocument: (id: string, title: string, kind?: PdfDocumentKind, documentId?: string) => void;
  addLoadingDocumentSilent: (id: string, title: string, kind?: PdfDocumentKind, documentId?: string) => void;
  openDocument: (doc: PdfDocument) => void;
  openDocumentSilent: (doc: PdfDocument) => void;
  closeDocument: (id: string) => void;
  closeOtherDocuments: (keepId: string) => void;
  closeAllDocuments: () => void;
  setActiveDocument: (id: string, activateSnippet?: string) => void;
  togglePanel: () => void;
  setPanelOpen: (open: boolean) => void;
  setPanelWidth: (width: number) => void;
  setSidebarView: (view: PdfSidebarView) => void;
  setPendingNavigation: (docId: string, nav: PdfPendingNavigation) => void;
  consumePendingNavigation: (docId: string) => PdfPendingNavigation | undefined;
  addCitationSnippet: (docId: string, snippet: string, page?: number, activate?: boolean) => void;
  setActiveCitationSnippet: (snippet: string | null) => void;
  /** Set/replace the cached diff HTML for a given version id. */
  setDiffHtml: (versionId: string, html: string | null) => void;
};

export const usePdfDocumentStore = create<PdfDocumentState>((set, get) => ({
  documents: [],
  activeDocumentId: null,
  panelOpen: false,
  panelWidth: DEFAULT_PANEL_WIDTH,
  sidebarView: "none",
  pendingNavigations: new Map(),
  citationSnippets: {},
  activeCitationSnippet: null,
  citationNavTick: 0,
  diffHtmlByVersion: {},

  addLoadingDocument(id, title, kind?, documentId?) {
    const { documents } = get();
    if (documents.find((d) => d.id === id)) {
      set({ activeDocumentId: id, panelOpen: true });
      return;
    }
    set({
      documents: [...documents, { id, title, loading: true, kind, documentId }],
      activeDocumentId: id,
      panelOpen: true,
    });
  },

  addLoadingDocumentSilent(id, title, kind?, documentId?) {
    const { documents, activeDocumentId } = get();
    if (documents.find((d) => d.id === id)) return;
    set({
      documents: [...documents, { id, title, loading: true, kind, documentId }],
      activeDocumentId: activeDocumentId ?? id,
    });
  },

  openDocument(doc) {
    const { documents, activeDocumentId } = get();
    const isOpeningGenerated = doc.kind === "generated";
    const hasGeneratedInList = documents.some((d) => d.kind === "generated");

    // Mode switch in either direction (citation<->generated) → wipe the documents
    // list + navigation state, then open the new doc solo. Generated docs never
    // coexist with citations in the tab strip. We check the entire list (not just
    // the active doc) because a loading placeholder may have shifted activeDocumentId
    // away from the generated doc before the fetch completes.
    //
    // citationSnippets is intentionally NOT wiped — entries are docId-keyed and
    // preserving them keeps in-turn click-after-takeover correct (e.g. a citation
    // arrives via SSE, a generated doc auto-opens and wipes the active citation,
    // then the user clicks the citation link in chat — the snippet must still be
    // in the store for the re-opened doc to highlight it). The PdfViewerCore
    // citation-highlight effect early-returns when active doc kind === "generated"
    // (defense-in-depth against stale snippets rendering over a generated doc).
    if (isOpeningGenerated || hasGeneratedInList) {
      for (const d of documents) {
        if (d.id !== doc.id) {
          try { d.pdfDocumentProxy?.destroy(); } catch { /* ignore */ }
        }
      }
      set({
        documents: [doc],
        activeDocumentId: doc.id,
        panelOpen: true,
        pendingNavigations: new Map(),
        activeCitationSnippet: null,
      });
      return;
    }

    // Citation → citation: keep the Phase 1 focus-yank-guard. Only steal active
    // focus if the user is still on this doc's loading tab, or if no doc is
    // active. Otherwise the user has moved on (clicked another citation,
    // switched tabs) and a finishing background load must not yank them back.
    const shouldTakeFocus = activeDocumentId === doc.id || activeDocumentId === null;
    const existing = documents.find((d) => d.id === doc.id);
    if (existing) {
      // Replace loading placeholder with real document.
      set({
        documents: documents.map((d) => (d.id === doc.id ? doc : d)),
        ...(shouldTakeFocus && { activeDocumentId: doc.id }),
        panelOpen: true,
      });
      return;
    }
    set({
      documents: [...documents, doc],
      ...(shouldTakeFocus && { activeDocumentId: doc.id }),
      panelOpen: true,
    });
  },

  openDocumentSilent(doc) {
    const { documents, activeDocumentId } = get();
    const existing = documents.find((d) => d.id === doc.id);
    if (existing) {
      set({
        documents: documents.map((d) => (d.id === doc.id ? doc : d)),
        activeDocumentId: activeDocumentId ?? doc.id,
      });
      return;
    }
    set({
      documents: [...documents, doc],
      activeDocumentId: activeDocumentId ?? doc.id,
    });
  },

  closeDocument(id) {
    const { documents, activeDocumentId, citationSnippets } = get();
    const doc = documents.find((d) => d.id === id);
    if (!doc) return;

    doc.pdfDocumentProxy?.destroy();
    const remaining = documents.filter((d) => d.id !== id);

    let nextActiveId: string | null = null;
    if (remaining.length > 0 && activeDocumentId === id) {
      const closedIdx = documents.findIndex((d) => d.id === id);
      nextActiveId =
        remaining[Math.min(closedIdx, remaining.length - 1)]?.id ?? null;
    } else if (activeDocumentId !== id) {
      nextActiveId = activeDocumentId;
    }

    const { [id]: _, ...remainingSnippets } = citationSnippets;
    const nextDocCitations = nextActiveId ? (remainingSnippets[nextActiveId] ?? []) : [];
    set({
      documents: remaining,
      activeDocumentId: nextActiveId,
      panelOpen: remaining.length > 0,
      citationSnippets: remainingSnippets,
      activeCitationSnippet: nextDocCitations[0]?.snippet ?? null,
    });
  },

  closeOtherDocuments(keepId) {
    const { documents, citationSnippets } = get();
    for (const doc of documents) {
      if (doc.id !== keepId) doc.pdfDocumentProxy?.destroy();
    }
    set({
      documents: documents.filter((d) => d.id === keepId),
      activeDocumentId: keepId,
      citationSnippets: keepId in citationSnippets ? { [keepId]: citationSnippets[keepId] } : {},
    });
  },

  closeAllDocuments() {
    const { documents } = get();
    for (const doc of documents) doc.pdfDocumentProxy?.destroy();
    set({
      documents: [],
      activeDocumentId: null,
      panelOpen: false,
      citationSnippets: {},
      pendingNavigations: new Map(),
      activeCitationSnippet: null,
    });
  },

  setActiveDocument(id, activateSnippet?) {
    const docCitations = get().citationSnippets[id];
    const snippet = activateSnippet ?? docCitations?.[0]?.snippet ?? null;
    console.debug("[citation-store] setActiveDocument:", id, "| snippet:", snippet?.slice(0, 60), activateSnippet ? "(explicit)" : "(default)");
    set((s) => ({
      activeDocumentId: id,
      activeCitationSnippet: snippet,
      citationNavTick: s.citationNavTick + 1,
    }));
  },

  togglePanel() {
    set((s) => ({ panelOpen: !s.panelOpen }));
  },

  setPanelOpen(open) {
    set({ panelOpen: open });
  },

  setPanelWidth(width) {
    setRaw(StorageKeys.pdfPanelWidth, String(width));
    set({ panelWidth: width });
  },

  setSidebarView(view) {
    set({ sidebarView: view });
  },

  setPendingNavigation(docId, nav) {
    const pending = new Map(get().pendingNavigations);
    pending.set(docId, nav);
    set({ pendingNavigations: pending });
  },

  consumePendingNavigation(docId) {
    const pending = get().pendingNavigations;
    const nav = pending.get(docId);
    if (nav) {
      const next = new Map(pending);
      next.delete(docId);
      set({ pendingNavigations: next });
    }
    return nav;
  },

  addCitationSnippet(docId, snippet, page, activate = true) {
    const current = get().citationSnippets;
    const docSnippets = current[docId] || [];
    if (docSnippets.some((c) => c.snippet === snippet)) {
      if (activate) {
        // Already stored — still activate so re-clicks work.
        console.debug("[citation-store] addCitationSnippet: deduplicated, re-activating snippet:", snippet?.slice(0, 60));
        set((s) => ({ activeCitationSnippet: snippet, citationNavTick: s.citationNavTick + 1 }));
      }
      return;
    }
    console.debug("[citation-store] addCitationSnippet: stored new snippet for doc", docId, ":", snippet?.slice(0, 60), activate ? "(active)" : "(silent)");
    set((s) => ({
      citationSnippets: {
        ...current,
        [docId]: [...docSnippets, { snippet, page }],
      },
      ...(activate && {
        activeCitationSnippet: snippet,
        citationNavTick: s.citationNavTick + 1,
      }),
    }));
  },

  setActiveCitationSnippet(snippet) {
    console.debug("[citation-store] setActiveCitationSnippet:", snippet?.slice(0, 60));
    set((s) => ({ activeCitationSnippet: snippet, citationNavTick: s.citationNavTick + 1 }));
  },

  setDiffHtml(versionId, html) {
    set((s) => ({ diffHtmlByVersion: { ...s.diffHtmlByVersion, [versionId]: html } }));
  },
}));

// Lazy-init persisted width on first client-side access.
if (typeof window !== "undefined") {
  usePdfDocumentStore.setState({ panelWidth: readPersistedWidth() });
}

/** Load a PDF from an ArrayBuffer and add it to the store. */
export async function loadPdfFromArrayBuffer(
  data: ArrayBuffer,
  title: string,
  id?: string,
  kind: PdfDocumentKind = "citation",
  versionId?: string,
  documentId?: string,
): Promise<PdfDocument> {
  const pdfjs = await getPdfJs();
  const loadingTask = pdfjs.getDocument({ data });
  const pdfDocumentProxy = await loadingTask.promise;

  const doc: PdfDocument = {
    id: id ?? crypto.randomUUID(),
    title,
    pdfDocumentProxy,
    pageCount: pdfDocumentProxy.numPages,
    kind,
    versionId,
    documentId,
  };

  usePdfDocumentStore.getState().openDocument(doc);
  return doc;
}

/** Load a PDF from an ArrayBuffer into the store without opening the panel. */
async function loadPdfFromArrayBufferSilent(
  data: ArrayBuffer,
  title: string,
  id?: string,
  kind: PdfDocumentKind = "citation",
): Promise<PdfDocument> {
  const pdfjs = await getPdfJs();
  const loadingTask = pdfjs.getDocument({ data });
  const pdfDocumentProxy = await loadingTask.promise;

  const doc: PdfDocument = {
    id: id ?? crypto.randomUUID(),
    title,
    pdfDocumentProxy,
    pageCount: pdfDocumentProxy.numPages,
    kind,
  };

  usePdfDocumentStore.getState().openDocumentSilent(doc);
  return doc;
}
